import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { ErrorState, Loading, PageHeader, Panel, SeverityBadge } from '../components/ui';
import { useAuth } from '../hooks/useAuth';
import { api, ApiError } from '../lib/api';

type SettingsResponse = {
  settings: { key: string; value: unknown; description: string | null }[];
  runtime: {
    appEnv: string;
    storeName: string;
    storeTimezone: string;
    openTime: string;
    closeTime: string;
    whatsappMode: string;
    emailMode: string;
    webpushMode: string;
    alertStormThreshold: number;
  };
};

type AlertRule = {
  id: string;
  name: string;
  eventType: string;
  zoneKind: string | null;
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  cooldownSeconds: number;
  channels: string[];
  conditions: Record<string, unknown>;
  enabled: boolean;
};

export function SettingsPage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const settingsQuery = useQuery({
    queryKey: ['system', 'settings'],
    queryFn: () => api.get<SettingsResponse>('/system/settings'),
  });

  const rulesQuery = useQuery({
    queryKey: ['system', 'alert-rules'],
    queryFn: () => api.get<{ items: AlertRule[] }>('/system/alert-rules'),
  });

  const updateSetting = useMutation({
    mutationFn: ({ key, value }: { key: string; value: string }) =>
      api.put(`/system/settings/${key}`, { value }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['system', 'settings'] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not save'),
  });

  const updateRule = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<AlertRule> }) =>
      api.patch(`/system/alert-rules/${id}`, body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['system', 'alert-rules'] }),
  });

  const reloadVision = useMutation({
    mutationFn: () => api.post('/system/vision/reload'),
  });

  if (settingsQuery.isLoading) return <Loading label="Loading settings" />;
  if (settingsQuery.isError) {
    return <ErrorState message={(settingsQuery.error as Error).message} onRetry={() => void settingsQuery.refetch()} />;
  }

  const { runtime } = settingsQuery.data!;
  const canManage = can('settings:manage');

  return (
    <div className="space-y-6">
      <PageHeader
        title="Settings"
        subtitle={`${runtime.storeName} · ${runtime.storeTimezone} · ${runtime.appEnv}`}
        actions={
          canManage ? (
            <button
              type="button"
              className="btn-secondary"
              onClick={() => reloadVision.mutate()}
              disabled={reloadVision.isPending}
            >
              <RefreshCw className="h-4 w-4" aria-hidden />
              Reload vision config
            </button>
          ) : null
        }
      />

      {/* Delivery modes — the single most consequential thing on this page */}
      <Panel title="Notification channels">
        <div className="grid gap-3 p-4 sm:grid-cols-3">
          <ModeCard label="WhatsApp" mode={runtime.whatsappMode} appEnv={runtime.appEnv} />
          <ModeCard label="Email" mode={runtime.emailMode} appEnv={runtime.appEnv} />
          <ModeCard label="Web push" mode={runtime.webpushMode} appEnv={runtime.appEnv} />
        </div>
        <p className="border-t border-line px-4 py-2.5 text-xs text-muted">
          Modes are set in the environment, not here — a channel cannot be switched to live from a
          browser session. In test mode messages are written to the delivery log and never sent.
        </p>
      </Panel>

      {/* Store hours */}
      <Panel title="Store hours">
        <div className="grid gap-4 p-4 sm:grid-cols-3">
          <TimeSetting
            label="Opening time"
            value={runtime.openTime}
            disabled={!canManage}
            onSave={(value) => updateSetting.mutate({ key: 'store_open_time', value })}
          />
          <TimeSetting
            label="Closing time"
            value={runtime.closeTime}
            disabled={!canManage}
            onSave={(value) => updateSetting.mutate({ key: 'store_close_time', value })}
          />
          <div>
            <p className="label mb-1.5">Timezone</p>
            <p className="font-mono text-sm">{runtime.storeTimezone}</p>
          </div>
        </div>
        <p className="border-t border-line px-4 py-2.5 text-xs text-muted">
          These define "after hours". A closing time earlier than the opening time is treated as
          spanning midnight, so late trading does not get flagged as intrusion.
        </p>
        {error ? <p className="px-4 pb-3 text-sm text-critical">{error}</p> : null}
      </Panel>

      {/* Alert rules */}
      <Panel title="Alert rules">
        {rulesQuery.isLoading ? (
          <Loading />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px]">
              <thead>
                <tr>
                  <th className="table-header">Rule</th>
                  <th className="table-header">Severity</th>
                  <th className="table-header">Cooldown</th>
                  <th className="table-header">Channels</th>
                  <th className="table-header">Conditions</th>
                  <th className="table-header">Enabled</th>
                </tr>
              </thead>
              <tbody>
                {(rulesQuery.data?.items ?? []).map((rule) => (
                  <tr key={rule.id}>
                    <td className="table-cell">
                      <p className="font-semibold">{rule.name}</p>
                      <p className="font-mono text-xs text-muted">
                        {rule.eventType}
                        {rule.zoneKind ? ` · ${rule.zoneKind}` : ''}
                      </p>
                    </td>
                    <td className="table-cell">
                      <SeverityBadge severity={rule.severity} />
                    </td>
                    <td className="table-cell">
                      {canManage ? (
                        <select
                          className="input w-auto py-1 text-xs"
                          value={rule.cooldownSeconds}
                          onChange={(e) =>
                            updateRule.mutate({ id: rule.id, body: { cooldownSeconds: Number(e.target.value) } })
                          }
                        >
                          {[0, 60, 300, 600, 900, 1800, 3600].map((seconds) => (
                            <option key={seconds} value={seconds}>
                              {seconds === 0 ? 'none' : `${seconds / 60} min`}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className="font-mono text-xs">{rule.cooldownSeconds}s</span>
                      )}
                    </td>
                    <td className="table-cell text-xs text-muted">{rule.channels.join(', ')}</td>
                    <td className="table-cell text-xs text-muted">
                      {Object.keys(rule.conditions).length === 0
                        ? '—'
                        : Object.entries(rule.conditions)
                            .map(([key, value]) => `${key}: ${String(value)}`)
                            .join(', ')}
                    </td>
                    <td className="table-cell">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-lime"
                        checked={rule.enabled}
                        disabled={!canManage}
                        onChange={(e) => updateRule.mutate({ id: rule.id, body: { enabled: e.target.checked } })}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="border-t border-line px-4 py-2.5 text-xs text-muted">
          Cooldowns are generous on purpose. The failure that destroys trust in a system like this is
          not a missed notification — it is thirty notifications for one situation at 2am, after which
          people mute the channel and miss the one that mattered.
        </p>
      </Panel>

      {/* Privacy statement — visible in the product, not just in a document */}
      <Panel title="Privacy posture">
        <ul className="space-y-2 p-4 text-sm text-muted">
          <li>· No facial recognition, face embeddings or biometric identification is performed.</li>
          <li>· People are tracked as anonymous per-camera ids that reset when a stream reconnects.</li>
          <li>· Tracks are never correlated between cameras, and no customer is identified by name.</li>
          <li>· Alert snapshots are deleted automatically after the retention period.</li>
          <li>· Camera viewing, configuration changes and alert responses are all recorded in the audit log.</li>
          <li>
            · Kitchen, billing and storage cameras record staff. A written monitoring policy and visible
            signage are required before go-live — the technical controls here do not discharge that.
          </li>
        </ul>
      </Panel>
    </div>
  );
}

function ModeCard({ label, mode, appEnv }: { label: string; mode: string; appEnv: string }) {
  const live = mode === 'live';
  // Test mode in production is the configuration that looks fine and quietly
  // delivers nothing, so it gets called out rather than shown as neutral.
  const warn = !live && appEnv === 'production';

  return (
    <div
      className={`rounded-lg border px-3 py-3 ${
        live ? 'border-online/40 bg-online/5' : warn ? 'border-high/40 bg-high/5' : 'border-line bg-ink/40'
      }`}
    >
      <p className="label">{label}</p>
      <p className={`mt-1 font-display text-lg ${live ? 'text-online' : warn ? 'text-high' : 'text-muted'}`}>
        {live ? 'Live' : 'Test'}
      </p>
      {warn ? <p className="mt-1 text-xs text-high">Production is running with this channel in test mode.</p> : null}
    </div>
  );
}

function TimeSetting({
  label,
  value,
  disabled,
  onSave,
}: {
  label: string;
  value: string;
  disabled: boolean;
  onSave: (value: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  return (
    <label className="block">
      <span className="label mb-1.5 block">{label}</span>
      <div className="flex gap-2">
        <input
          type="time"
          className="input"
          value={draft}
          disabled={disabled}
          onChange={(e) => setDraft(e.target.value)}
        />
        {!disabled && draft !== value ? (
          <button type="button" className="btn-primary shrink-0" onClick={() => onSave(draft)}>
            Save
          </button>
        ) : null}
      </div>
    </label>
  );
}
